/**
 * Theme: the quick view (snippets/core-quick-view.liquid), on every page.
 *
 * OPENED BY any [data-qv-open] inside an element carrying data-qv-handle: the
 * product page's multi-buy cards, and the cart drawer's upsell cards for any
 * product with more than one variant. In a group marked data-qv-cards (the
 * multi-buy rail) a plain click on the card's own link opens it too. The nearest [data-qv-group] around the
 * trigger is the list the arrows and swipes move through, in DOM order, hidden
 * items skipped.
 *
 * NEVER A BLIND ADD. Every option with a real choice must be picked; nothing is
 * preselected but the colour the server already showed.
 *
 * WHAT IT DOES NOT DO: work out a price. The body (sections/core-multibuy-view.
 * liquid) ships every variant's price and its display price for every bag count;
 * this file picks one. The tracker is copied in by assets/core-cart.js from the
 * cart drawer's fresh render after each change.
 *
 * Adds go through window.CORECart.add with { quiet: true }, so the drawer is
 * re-rendered but never opens: the shopper stays here.
 */
(function () {
  'use strict';

  var dialog = document.querySelector('[data-qv-dialog]');
  if (!dialog || dialog.__qvBound) return;
  dialog.__qvBound = true;

  var VIEW_SECTION = dialog.getAttribute('data-view-section') || 'core-multibuy-view';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var phone = window.matchMedia('(max-width: 750px)');
  var panel = dialog.querySelector('[data-qv-panel]');
  var body = dialog.querySelector('[data-qv-body]');
  var countEl = dialog.querySelector('[data-qv-count]');
  var list = [];
  var index = 0;
  var opener = null;
  var openerHandle = null;
  var openerGroup = null;
  var cache = {};
  var guide = null; // { drawer, original, current, imported } while a borrowed guide is in place
  // EDIT MODE: the bag line being edited ({ key, variant, qty, props }), or null.
  var edit = null;

  function routeRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  /** Items in bag, gift cards excluded, as the tracker last rendered it. */
  function bagCount() {
    var t = dialog.querySelector('[data-tracker]');
    return t ? parseInt(t.getAttribute('data-count'), 10) || 0 : 0;
  }

  function url(handle) {
    return routeRoot() + 'products/' + encodeURIComponent(handle) + '?section_id=' + VIEW_SECTION;
  }

  function fetchView(handle) {
    if (cache[handle]) return cache[handle];
    cache[handle] = fetch(url(handle), { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .catch(function (error) {
        delete cache[handle];
        throw error;
      });
    return cache[handle];
  }

  function handleAt(i) {
    return list[i] || null;
  }

  function paintSteps() {
    dialog.querySelectorAll('[data-qv-step]').forEach(function (button) {
      var dir = Number(button.getAttribute('data-qv-step'));
      button.disabled = dir < 0 ? index <= 0 : index >= list.length - 1;
    });
    countEl.textContent = list.length > 1 ? index + 1 + ' of ' + list.length : '';
  }

  function load(i) {
    index = i;
    restoreGuide();
    paintSteps();
    var handle = handleAt(i);
    body.setAttribute('aria-busy', 'true');
    return fetchView(handle)
      .then(function (html) {
        if (handleAt(index) !== handle) return; // stepped on since
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var product = doc.querySelector('[data-qv-product]');
        body.innerHTML = '';
        if (product) body.appendChild(document.importNode(product, true));
        body.scrollTop = 0;
        paintSteps();
        initProduct(body.querySelector('[data-qv-product]'), edit);
        // Warm the neighbours so stepping is instant.
        [index - 1, index + 1].forEach(function (n) {
          var h = handleAt(n);
          if (h) fetchView(h).catch(function () {});
        });
      })
      .catch(function () {
        body.innerHTML = '';
        var p = document.createElement('p');
        p.className = 'core-qvp__fail';
        p.textContent = 'This product could not be loaded. ';
        var a = document.createElement('a');
        a.href = routeRoot() + 'products/' + encodeURIComponent(handle);
        a.textContent = 'View it here';
        p.appendChild(a);
        body.appendChild(p);
      })
      .then(function () {
        body.removeAttribute('aria-busy');
      });
  }

  function itemsOf(group) {
    return Array.prototype.filter.call(group.querySelectorAll('[data-qv-handle]'), function (item) {
      return !item.closest('[hidden]');
    });
  }

  /* THE PHONE SHEET'S IMAGE SIZE, from the window alone: the sheet's height less
     the chrome above the body and a fixed allowance for the image row, a
     two-line title, the price, the display price and the pinned Add to bag bar.
     So the image, its row, the title and the price are on the first screen, and
     the size never changes from one product to the next. Desktop sizes in CSS. */
  // row 48, gap 18, two-line title 46, price row 32, display price 22, a margin
  // 14, and the pinned Add to bag bar with its padding 96. Measured: at 242 the
  // price sat ~10px behind the Add bar at 375x667.
  var SHEET_ALLOWANCE = 48 + 18 + 46 + 32 + 22 + 14 + 96;
  function sizeSheet() {
    if (!phone.matches || !dialog.open) {
      dialog.style.removeProperty('--qv-img-h');
      return;
    }
    var above = body.getBoundingClientRect().top - panel.getBoundingClientRect().top;
    var maxByWidth = (panel.clientWidth - 36) * 4 / 3;
    var h = Math.max(160, Math.min(maxByWidth, panel.clientHeight - above - SHEET_ALLOWANCE));
    dialog.style.setProperty('--qv-img-h', Math.floor(h) + 'px');
  }
  window.addEventListener('resize', sizeSheet);

  function open(trigger) {
    var item = trigger.closest('[data-qv-handle]');
    if (!item) return;
    // EDIT MODE for a bag line (cart drawer or cart page): this one product,
    // its current variant preselected, "Update bag" instead of "Add to bag".
    edit = null;
    if (item.hasAttribute('data-qv-edit-key')) {
      var props = {};
      try {
        props = JSON.parse(item.getAttribute('data-qv-edit-props') || '{}') || {};
      } catch (error) {
        props = {};
      }
      edit = {
        key: item.getAttribute('data-qv-edit-key'),
        variant: Number(item.getAttribute('data-qv-edit-variant')),
        qty: Number(item.getAttribute('data-qv-edit-qty')) || 1,
        props: props,
      };
    }
    dialog.toggleAttribute('data-mode', false);
    if (edit) dialog.setAttribute('data-mode', 'edit');
    var group = edit ? null : trigger.closest('[data-qv-group]');
    var items = group ? itemsOf(group) : [item];
    list = items.map(function (el) {
      return el.getAttribute('data-qv-handle');
    });
    var i = items.indexOf(item);
    if (i < 0) return;
    opener = trigger;
    openerHandle = item.getAttribute('data-qv-handle');
    openerGroup = group;
    // Published for the multi-buy rail, which holds back hiding in-bag cards
    // while the quick view is stepping through it (assets/core-multibuy.js).
    dialog.qvGroup = group;
    panel.style.transform = '';
    dialog.showModal();
    sizeSheet();
    load(i);
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', function () {
    restoreGuide();
    body.innerHTML = '';
    // The opener can have been replaced by a re-render (the cart drawer
    // re-renders on every add); fall back to the same product's trigger.
    var target = opener && opener.isConnected ? opener : null;
    if (!target && edit) {
      // The line was re-rendered (and its key may have changed on an update):
      // its title link is where focus goes back to.
      var line = document.querySelector('[data-qv-edit-key="' + edit.key + '"]');
      target = line && line.querySelector('.ci-title');
    }
    if (!target && openerGroup && openerGroup.isConnected) {
      var again = openerGroup.querySelector('[data-qv-handle="' + openerHandle + '"] [data-qv-open], [data-qv-handle="' + openerHandle + '"][data-qv-open]');
      target = again || openerGroup.querySelector('button, a');
    }
    if (target) target.focus();
  });

  function step(dir) {
    var next = index + dir;
    if (next < 0 || next >= list.length) return;
    load(next);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-qv-open]');
    if (trigger) {
      event.preventDefault();
      open(trigger);
      return;
    }
    // A CARD'S OWN LINK, in a group marked data-qv-cards (the product page's
    // multi-buy rail): a plain click or Enter opens the quick view on top of the
    // page instead of leaving it. The link stays a real link to the product, so
    // Ctrl/Cmd/Shift-click, a middle click (auxclick, never seen here) and
    // "open in new tab" behave as normal, and without this script it simply
    // navigates. Focus returns to the link when the quick view closes.
    // A bag line's image or title link (data-qv-edit-key) opens edit mode the
    // same way; the stepper and Remove are buttons and never reach here.
    var link = event.target.closest('a[href]');
    if (!link || !(link.closest('[data-qv-cards] [data-qv-handle]') || link.closest('[data-qv-edit-key]'))) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    open(link);
  });

  dialog.addEventListener('click', function (event) {
    // A click on the dialog box itself is a click on the dimmed backdrop.
    if (event.target === dialog || event.target.closest('[data-qv-close]')) {
      close();
      return;
    }
    var stepper = event.target.closest('[data-qv-step]');
    if (stepper && !stepper.disabled) step(Number(stepper.getAttribute('data-qv-step')));
  });

  dialog.addEventListener('keydown', function (event) {
    if (!event.target.closest('[data-qv-step], .core-qv__top')) return;
    if (event.key === 'ArrowRight') step(1);
    if (event.key === 'ArrowLeft') step(-1);
  });

  // ---- swipe: down on the sheet's head closes it; sideways on the product
  // details moves between products. The gallery keeps its own native swipe.
  var drag = null;
  dialog.addEventListener('pointerdown', function (event) {
    if (event.pointerType === 'mouse') return;
    var onHead = phone.matches && event.target.closest('[data-qv-grab], .core-qv__top');
    var onInfo = event.target.closest('.core-qvp__info');
    if (!onHead && !onInfo) return;
    drag = { x: event.clientX, y: event.clientY, head: !!onHead, id: event.pointerId };
  });
  dialog.addEventListener('pointermove', function (event) {
    if (!drag || event.pointerId !== drag.id) return;
    var dy = event.clientY - drag.y;
    if (drag.head && dy > 0) panel.style.transform = 'translateY(' + dy + 'px)';
  });
  dialog.addEventListener('pointerup', function (event) {
    if (!drag || event.pointerId !== drag.id) return;
    var dx = event.clientX - drag.x;
    var dy = event.clientY - drag.y;
    var wasHead = drag.head;
    drag = null;
    panel.style.transform = '';
    if (wasHead && dy > 90 && Math.abs(dy) > Math.abs(dx)) {
      close();
      return;
    }
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
  });
  dialog.addEventListener('pointercancel', function () {
    drag = null;
    panel.style.transform = '';
  });

  // ---- the bag badge follows the header's count
  document.addEventListener('shopify:cart:lines-update', function (event) {
    if (!event.promise || typeof event.promise.then !== 'function') return;
    event.promise.then(function (result) {
      var n = result && result.cart && typeof result.cart.totalQuantity === 'number'
        ? result.cart.totalQuantity
        : result && result.detail ? result.detail.itemCount : null;
      if (typeof n !== 'number') return;
      var badge = dialog.querySelector('[data-qv-badge]');
      if (badge) {
        badge.textContent = String(n);
        badge.hidden = n === 0;
      }
      var bag = dialog.querySelector('[data-qv-bag]');
      if (bag) bag.setAttribute('aria-label', n + ' in your bag');
    }).catch(function () {});
  });

  // ---- after a cart change, the display price for the new bag count
  document.addEventListener('core:offer-sync', function () {
    var product = body.querySelector('[data-qv-product]');
    if (product && typeof product.__paint === 'function') product.__paint();
  });

  /* ---- the borrowed size guide --------------------------------------------
     The page has at most one #core-size-guide drawer (the product page's own,
     when that product has a chart). The quick view product's guide body is
     moved into it while it is open and the page's own body put back when the
     quick view moves on or closes. With no drawer on the page, the quick view
     product's whole drawer is imported and removed again afterwards. */
  function restoreGuide() {
    if (!guide) return;
    if (guide.imported) {
      guide.drawer.remove();
    } else if (guide.original && guide.current) {
      guide.current.replaceWith(guide.original);
    }
    guide = null;
  }

  function ensureGuideScript() {
    if (window.CORESizeGuide) return Promise.resolve();
    var src = dialog.getAttribute('data-guide-script');
    if (!src) return Promise.resolve();
    return new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = resolve;
      document.head.appendChild(s);
    });
  }

  function openGuide(product) {
    var template = product.querySelector('template[data-qv-guide]');
    if (!template) return;
    var incoming = template.content.querySelector('#core-size-guide');
    if (!incoming) return;
    restoreGuide();
    var drawer = document.getElementById('core-size-guide');
    if (drawer) {
      var original = drawer.querySelector('.core-sizetool__body');
      var next = document.importNode(incoming.querySelector('.core-sizetool__body'), true);
      if (!original || !next) return;
      original.replaceWith(next);
      guide = { drawer: drawer, original: original, current: next, imported: false };
    } else {
      drawer = document.importNode(incoming, true);
      document.body.appendChild(drawer);
      guide = { drawer: drawer, imported: true };
    }
    ensureGuideScript().then(function () {
      var bodyEl = drawer.querySelector('.core-sizetool__body');
      if (window.CORESizeGuide && bodyEl) window.CORESizeGuide.bind(bodyEl);
      customElements.whenDefined('theme-drawer').then(function () {
        if (typeof drawer.open === 'function') drawer.open();
      });
    });
  }

  /* ---- one product's options, price and add ------------------------------ */
  function initProduct(product, editing) {
    if (!product) return;
    var variants = [];
    try {
      variants = JSON.parse(product.querySelector('[data-qv-variants]').textContent);
    } catch (error) {
      variants = [];
    }
    var groups = Array.prototype.slice.call(product.querySelectorAll('[data-qv-option]'));
    var optionCount = variants.length ? variants[0].options.length : 0;
    var selected = [];
    for (var o = 0; o < optionCount; o++) selected.push(null);

    // An option with no real choice (one value) is decided already.
    for (var oi = 0; oi < optionCount; oi++) {
      var values = {};
      variants.forEach(function (v) {
        values[v.options[oi]] = true;
      });
      if (Object.keys(values).length === 1) selected[oi] = variants[0].options[oi];
    }
    // The colour arrives preselected, as the server checked it.
    groups.forEach(function (group) {
      var idx = Number(group.getAttribute('data-qv-option'));
      var on = group.querySelector('[data-qv-pick][aria-checked="true"]');
      if (on) selected[idx] = on.getAttribute('data-qv-pick');
    });

    var priceEl = product.querySelector('[data-qv-price]');
    var tierEl = product.querySelector('[data-qv-tier]');
    var errorEl = product.querySelector('[data-qv-error]');
    var addedEl = product.querySelector('[data-qv-added]');
    var addButton = product.querySelector('[data-qv-add]');
    var gallery = product.querySelector('[data-qv-gallery]');
    var colourPos = Number(product.getAttribute('data-colour-pos')) - 1;

    // EDIT MODE: start from the bag line's own variant, every option checked.
    var editVariant = editing ? variants.filter(function (v) { return v.id === editing.variant; })[0] : null;
    if (editVariant) {
      selected = editVariant.options.slice();
      groups.forEach(function (group) {
        var idx = Number(group.getAttribute('data-qv-option'));
        group.querySelectorAll('[data-qv-pick]').forEach(function (b) {
          b.setAttribute('aria-checked', b.getAttribute('data-qv-pick') === selected[idx] ? 'true' : 'false');
        });
      });
    }

    function match(partial) {
      return variants.filter(function (v) {
        for (var i = 0; i < optionCount; i++) {
          if (partial[i] !== null && v.options[i] !== partial[i]) return false;
        }
        return true;
      });
    }

    function current() {
      if (selected.indexOf(null) !== -1) return null;
      return match(selected)[0] || null;
    }

    function paint() {
      var v = current();
      // Price: the chosen variant, else the first one the selection still allows.
      var shown = v || match(selected)[0] || variants[0];
      if (shown) {
        priceEl.innerHTML = shown.price;
        if (shown.tiers && shown.tiers.length) {
          tierEl.innerHTML = shown.tiers[Math.min(bagCount(), shown.tiers.length - 1)];
        }
      }
      // Availability on every non-colour button, against the other choices.
      groups.forEach(function (group) {
        var idx = Number(group.getAttribute('data-qv-option'));
        if (idx === colourPos) return;
        group.querySelectorAll('[data-qv-pick]').forEach(function (b) {
          var probe = selected.slice();
          probe[idx] = b.getAttribute('data-qv-pick');
          var any = match(probe).some(function (m) {
            return m.available;
          });
          b.setAttribute('data-available', any ? 'true' : 'false');
        });
      });
      var colourValue = product.querySelector('[data-qv-name="colour"] [data-qv-value]');
      if (colourValue && colourPos >= 0) colourValue.textContent = selected[colourPos] || '';
      if (editing && addButton && !addButton.hasAttribute('data-busy')) {
        // Nothing to update until a different variant is chosen.
        addButton.textContent = 'Update bag';
        addButton.disabled = !(v && v.id !== editing.variant);
      } else if (addButton && !addButton.disabled) {
        addButton.textContent = v && !v.available ? 'Sold Out' : 'Add to bag';
      }
    }
    product.__paint = paint;

    function showMedia(i) {
      if (!gallery || i < 0) return;
      var slide = gallery.children[i];
      if (slide) gallery.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    groups.forEach(function (group) {
      var idx = Number(group.getAttribute('data-qv-option'));
      group.addEventListener('click', function (event) {
        var pick = event.target.closest('[data-qv-pick]');
        if (!pick) return;
        group.querySelectorAll('[data-qv-pick]').forEach(function (b) {
          b.setAttribute('aria-checked', b === pick ? 'true' : 'false');
        });
        selected[idx] = pick.getAttribute('data-qv-pick');
        errorEl.hidden = true;
        addedEl.hidden = true;
        if (idx === colourPos) {
          var withMedia = match(selected).filter(function (m) {
            return m.media >= 0;
          })[0];
          if (withMedia) showMedia(withMedia.media);
        }
        paint();
      });
    });

    // ---- gallery: the row under the photo (prev, dots, next) follows the
    // scroll. IT LOOPS: next on the last image goes to the first and previous on
    // the first to the last, by arrow, by a click on either half of the photo (a
    // fine pointer, with a matching cursor) and by a swipe past either end. The
    // arrows therefore never grey out. Touch keeps the native swipe in between.
    if (gallery) {
      var dots = product.querySelectorAll('[data-qv-dots] span');
      var slides = gallery.children.length;
      var fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      var ticking = false;
      var at = function () {
        return Math.round(gallery.scrollLeft / Math.max(1, gallery.clientWidth));
      };
      var paintGallery = function () {
        var i = at();
        dots.forEach(function (d, n) {
          d.classList.toggle('is-on', n === i);
        });
      };
      var goImage = function (dir) {
        if (slides < 2) return;
        var next = (at() + dir + slides) % slides;
        gallery.scrollTo({ left: gallery.clientWidth * next, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      };
      // A double or triple click on an arrow or an image half would otherwise
      // select the nearest text (the title). Cancelling the second and later
      // presses' default stops the selection; the first press still focuses
      // the arrow, so focus stays on the control that was clicked.
      var media = product.querySelector('.core-qvp__media');
      if (media) {
        media.addEventListener('mousedown', function (event) {
          if (event.detail > 1) event.preventDefault();
        });
      }
      gallery.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          paintGallery();
        });
      });
      product.querySelectorAll('[data-qv-img]').forEach(function (b) {
        b.addEventListener('click', function () {
          goImage(Number(b.getAttribute('data-qv-img')));
        });
      });
      if (slides > 1) {
        gallery.addEventListener('mousemove', function (event) {
          if (!fine.matches) return;
          var box = gallery.getBoundingClientRect();
          var half = event.clientX < box.left + box.width / 2 ? 'prev' : 'next';
          if (gallery.getAttribute('data-half') !== half) gallery.setAttribute('data-half', half);
        });
        gallery.addEventListener('mouseleave', function () {
          gallery.removeAttribute('data-half');
        });
        gallery.addEventListener('click', function (event) {
          if (!fine.matches) return;
          var box = gallery.getBoundingClientRect();
          goImage(event.clientX < box.left + box.width / 2 ? -1 : 1);
        });
        // A swipe past either end: the native scroll cannot go further, so a
        // horizontal swipe that started on the last (or first) image and left it
        // unmoved wraps round.
        var swipe = null;
        gallery.addEventListener('touchstart', function (event) {
          var t = event.touches[0];
          swipe = { x: t.clientX, y: t.clientY, i: at() };
        }, { passive: true });
        gallery.addEventListener('touchend', function (event) {
          if (!swipe) return;
          var t = event.changedTouches[0];
          var dx = t.clientX - swipe.x;
          var dy = t.clientY - swipe.y;
          var from = swipe.i;
          swipe = null;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
          window.setTimeout(function () {
            if (at() !== from) return;
            if (dx < 0 && from === slides - 1) goImage(1);
            if (dx > 0 && from === 0) goImage(-1);
          }, 250);
        }, { passive: true });
      }
      paintGallery();
    }

    var guideButton = product.querySelector('[data-qv-guide-open]');
    if (guideButton) {
      guideButton.addEventListener('click', function () {
        openGuide(product);
      });
    }

    function say(text) {
      errorEl.textContent = text;
      errorEl.hidden = false;
    }

    if (addButton) {
      addButton.addEventListener('click', function () {
        addedEl.hidden = true;
        var missing = selected.indexOf(null);
        if (missing !== -1) {
          var group = product.querySelector('[data-qv-option="' + missing + '"]');
          var name = group ? group.getAttribute('data-qv-name') : 'option';
          say('Please choose a ' + name + '.');
          var first = group && group.querySelector('[data-qv-pick]');
          if (first) first.focus();
          return;
        }
        var v = current();
        if (!v || !v.available) {
          say('That one is sold out. Please choose another.');
          return;
        }
        if (!window.CORECart) return;
        if (editing) {
          updateLine(v);
          return;
        }
        errorEl.hidden = true;
        addButton.disabled = true;
        addButton.textContent = 'Adding';
        window.CORECart.add(v.id, 1, { quiet: true }).then(function (data) {
          addButton.disabled = false;
          addButton.textContent = 'Add to bag';
          if (data && typeof data.status === 'number' && data.status >= 400) {
            say(data.description || data.message || 'That could not be added. Please try again.');
            return;
          }
          confirmAdded(addedEl);
          paint();
        });
      });
    }

    /* EDIT MODE: swap the bag line for the chosen variant at the SAME quantity.
       One /cart/update.js request (old line to 0, new variant to the line's
       quantity, merged with any line already holding that variant), with the
       drawer and cart page sections in it. A line with properties cannot carry
       them through update.js, so it is re-added with them and the old line then
       removed. If Shopify refuses (422) the bag is untouched; if it accepted but
       capped the quantity (not enough stock), the old line is put back, so a
       failed update never leaves the bag half changed. */
    function updateLine(v) {
      errorEl.hidden = true;
      addedEl.hidden = true;
      addButton.setAttribute('data-busy', '');
      addButton.disabled = true;
      addButton.textContent = 'Updating';
      var done = function () {
        addButton.removeAttribute('data-busy');
        paint();
      };
      var fail = function (text) {
        say(text || 'That could not be updated. Please try again.');
        done();
      };
      fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          var line = cart.items.filter(function (i) { return i.key === editing.key; })[0];
          if (!line) return fail('This item is no longer in your bag.');
          var qty = line.quantity;
          var props = line.properties || {};
          var hasProps = Object.keys(props).some(function (k) { return props[k] !== null && props[k] !== ''; });
          var existing = cart.items.filter(function (i) { return i.variant_id === v.id && i.key !== line.key; })[0];
          var before = existing ? existing.quantity : 0;
          var expected = before + qty;
          var request;
          if (!hasProps) {
            var updates = {};
            updates[line.key] = 0;
            updates[v.id] = expected;
            request = window.CORECart.update(updates);
          } else {
            request = window.CORECart.add(v.id, qty, { quiet: true, properties: props }).then(function (data) {
              if (data && typeof data.status === 'number' && data.status >= 400) return data;
              var gone = {};
              gone[line.key] = 0;
              return window.CORECart.update(gone);
            });
          }
          return request.then(function (data) {
            if (data && typeof data.status === 'number' && data.status >= 400) {
              return fail(data.description || data.message);
            }
            var items = (data && data.items) || [];
            var now = items.filter(function (i) { return i.variant_id === v.id; });
            var nowQty = now.reduce(function (n, i) { return n + i.quantity; }, 0);
            if (!hasProps && nowQty !== expected) {
              // Accepted but capped by stock: put the bag back as it was.
              var revert = {};
              revert[v.id] = before;
              revert[line.variant_id] = qty;
              return window.CORECart.update(revert).then(function () {
                fail('Sorry, there are not enough of that one in stock.');
              });
            }
            var newLine = now[0];
            editing.key = newLine ? newLine.key : editing.key;
            editing.variant = v.id;
            editing.qty = newLine ? newLine.quantity : qty;
            addedEl.innerHTML = '';
            var lead = document.createElement('strong');
            lead.textContent = 'Bag updated.';
            addedEl.appendChild(lead);
            addedEl.hidden = false;
            done();
          });
        })
        .catch(function () {
          fail();
        });
    }

    // Edit mode opens the gallery on the bag item's own colour, instantly.
    if (editVariant && editVariant.media >= 0 && gallery) {
      window.requestAnimationFrame(function () {
        var slide = gallery.children[editVariant.media];
        if (slide) gallery.scrollLeft = slide.offsetLeft;
      });
    }

    paint();
  }

  /* After an add the tracker at the top has been copied in from the drawer's
     fresh render by assets/core-cart.js. The confirmation repeats its message
     and saving beside the button, where the shopper is looking. */
  function confirmAdded(addedEl) {
    var tracker = dialog.querySelector('.core-qv__top [data-tracker]');
    var saving = tracker && tracker.querySelector('.core-tracker__saving');
    var message = tracker && tracker.querySelector('.core-tracker__msg');
    addedEl.innerHTML = '';
    var lead = document.createElement('strong');
    lead.textContent = 'Added to your bag.';
    addedEl.appendChild(lead);
    [message, saving].forEach(function (node) {
      if (!node || !node.textContent.trim()) return;
      var line = document.createElement('span');
      line.textContent = node.textContent.trim();
      if (node === saving) line.className = 'core-qvp__added-saving';
      addedEl.appendChild(line);
    });
    addedEl.hidden = false;
  }
})();
